import { useEffect, useState } from "react";
import api from "../lib/api";
import { Loader2, Target, ArrowRight, AlertTriangle, CheckCircle2, Lightbulb, Clock } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";
import { TARGET_ROLE_GROUPS } from "../constants/targetRoles";

export default function SkillGapPage() {
  const [resumes, setResumes] = useState([]);
  const [resumeId, setResumeId] = useState("");
  const [role, setRole] = useState("Software Engineer");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAll = async () => {
    const [a, b] = await Promise.all([api.get("/resume/list"), api.get("/skillgap/list")]);
    setResumes(a.data.items || []);
    setHistory(b.data.items || []);
    if (!resumeId && (a.data.items || []).length) setResumeId(a.data.items[0].resume_id);
  };
  useEffect(() => { loadAll(); }, []);

  const analyze = async () => {
    if (!resumeId) { toast.error("Analyze a resume first"); return; }
    setLoading(true); setReport(null);
    try {
      const r = await api.post("/skillgap/analyze", { resume_id: resumeId, target_role: role });
      setReport(r.data);
      toast.success("Skill gap report generated");
      loadAll();
    } catch (e) {
      apiErrorToast(e, "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="skillgap-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Skill Gap Analyzer</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">What&apos;s between you and the offer?</h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">Pick a target role. We compare your resume&apos;s skills against market expectations and return a learning roadmap.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-flat p-6 lg:col-span-1">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Configure</div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Resume</label>
              <select data-testid="skillgap-resume-select" className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white" value={resumeId} onChange={(e)=>setResumeId(e.target.value)}>
                {resumes.length === 0 && <option value="">No resumes — upload one first</option>}
                {resumes.map(r => <option key={r.id} value={r.resume_id}>{r.target_role} · {new Date(r.created_at).toLocaleDateString()}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Target role</label>
              <select data-testid="skillgap-role-select" className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white" value={role} onChange={(e)=>setRole(e.target.value)}>
                {TARGET_ROLE_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <button data-testid="skillgap-run-btn" onClick={analyze} disabled={loading || !resumeId} className="w-full h-11 rounded-md bg-[#0E7C66] text-white font-semibold hover:bg-[#0A5E4D] transition flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> : <>Run analysis <ArrowRight size={14} /></>}
            </button>
          </div>

          {history.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">History</div>
              <div className="space-y-2">
                {history.slice(0,5).map(h => (
                  <button key={h.id} onClick={()=>setReport(h)} className="w-full text-left p-3 rounded-md border border-zinc-200 hover:border-zinc-900 transition" data-testid={`skillgap-history-${h.id}`}>
                    <div className="text-sm font-semibold">{h.target_role}</div>
                    <div className="text-xs text-zinc-500 flex items-center justify-between mt-1">
                      <span>{new Date(h.created_at).toLocaleString()}</span>
                      <span className="font-mono font-semibold">{h.match_percent}%</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-flat p-6 lg:col-span-2 min-h-[400px]" data-testid="skillgap-results">
          {!report ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="h-12 w-12 rounded-full bg-zinc-100 flex items-center justify-center"><Target size={20} className="text-zinc-500" /></div>
              <div className="font-display font-bold mt-4 text-lg">Your gap analysis lives here.</div>
              <p className="text-sm text-zinc-500 max-w-sm mt-1">Select a resume and target role to start.</p>
            </div>
          ) : <Report r={report} />}
        </div>
      </div>
    </div>
  );
}

function Report({ r }) {
  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{r.target_role}</div>
          <div className="font-display font-black text-5xl mt-1">{r.match_percent}<span className="text-zinc-400 text-xl">%</span></div>
          <div className="text-xs text-zinc-500 mt-1">Skill match</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} className="text-zinc-500" />
          <span>~{r.estimated_time_weeks || 0} weeks to close gap</span>
        </div>
      </div>
      <div className="meter-track mt-4">
        <div className="meter-fill" style={{ width: `${r.match_percent}%`, background: r.match_percent>=70?'#22C55E':r.match_percent>=40?'#EAB308':'#EF4444' }} />
      </div>
      {r.summary && <p className="text-sm text-zinc-700 mt-4 leading-relaxed">{r.summary}</p>}

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <SkillList title="You have" items={r.current_skills} variant="success" icon={CheckCircle2} testid="skills-current" />
        <SkillList title="Missing" items={r.missing_skills} variant="warn" icon={AlertTriangle} testid="skills-missing" />
        <SkillList title="Critical missing" items={r.critical_missing} variant="danger" icon={AlertTriangle} testid="skills-critical" />
        <SkillList title="Recommended" items={r.recommended_skills} variant="info" icon={Lightbulb} testid="skills-recommended" />
      </div>

      {(r.roadmap || []).length > 0 && (
        <div className="mt-8 pt-6 border-t border-zinc-200">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Learning roadmap</div>
          <ol className="space-y-3">
            {r.roadmap.map((step, i) => (
              <li key={i} className="flex items-start gap-3 p-3 border border-zinc-200 rounded-lg">
                <div className="font-mono text-xs px-2 py-1 rounded bg-[#0A0A0A] text-white shrink-0">{String(i+1).padStart(2,'0')}</div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{step.step}</div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    {step.duration_weeks && <span>{step.duration_weeks}w</span>}
                    {step.resources && <span>· {step.resources}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function SkillList({ title, items, variant, icon: Icon, testid }) {
  const tagCls = variant === 'success' ? 'tag-success' : variant === 'warn' ? 'tag-warn' : variant === 'danger' ? 'tag-danger' : 'tag-info';
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
        <Icon size={12} /> {title}
      </div>
      <div className="flex flex-wrap gap-2">
        {(items || []).length === 0 && <span className="text-xs text-zinc-400">—</span>}
        {(items || []).map((s, i) => <span key={i} className={`tag ${tagCls}`}>{s}</span>)}
      </div>
    </div>
  );
}
